/**
 * ApiClientProvider — builds the typed @ken/api-client once auth state is
 * available and places it in React context (via the shared provider in
 * @ken/api-client so module packages can read it too).
 *
 * Per KEN_LAYER_7 §6.4 + ADR-030. The client is recreated when the tenant
 * id changes so every request after a tenant switch sends the new
 * `X-Ken-Tenant` header. The JWT lives in the HttpOnly cookie, so
 * `getAccessToken` returns undefined here — the browser attaches the cookie
 * automatically on cross-origin and same-origin fetches.
 *
 * Modules MUST consume the client via `useApiClient()` from @ken/api-client.
 * Direct `fetch()` calls in module code are blocked by ESLint rule
 * `ken/no-direct-fetch` (KEN_LAYER_7 §9 I-26).
 */

import { useAuthStore } from "@ken/auth";
import type { AuthContext } from "@ken/api-client";
import {
  ApiClientProvider as SharedApiClientProvider,
  createApiClient,
  useApiClient as sharedUseApiClient,
} from "@ken/api-client";
import { useMemo, type ReactElement, type ReactNode } from "react";

const API_BASE_URL =
  (typeof window !== "undefined" &&
    (window as { __KEN_API_BASE_URL__?: string }).__KEN_API_BASE_URL__) ||
  "";

export function ApiClientProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const tenantId = useAuthStore((s) => s.tenantId);

  const client = useMemo(() => {
    const authContext: AuthContext = {
      tenantId,
      // JWT lives in the HttpOnly cookie; the browser sends it automatically.
      getAccessToken: () => undefined,
    };
    return createApiClient({ baseUrl: API_BASE_URL, authContext });
  }, [tenantId]);

  return <SharedApiClientProvider client={client}>{children}</SharedApiClientProvider>;
}

// Re-export the hook so existing cockpit-internal callers (ConciergeDock,
// etc.) keep working without an import-path change. New module code
// imports `useApiClient` from `@ken/api-client` directly.
export const useApiClient = sharedUseApiClient;
